/**
 * Stepper di stato dell'intestazione v4 (`Centro Ticket v4.dc.html`
 * L386-L393, L1007). SOLO indicatore visivo (decisione Q4): non e cliccabile,
 * i cambi di stato restano nel menu ⋯ / nelle Proprieta.
 *
 * Differenze volute dal prototipo (Q4):
 * - il percorso principale e Aperto → Preso in carico → In lavorazione →
 *   Risolto → **Chiuso**: «Chiuso» e l'ultimo passo, con la sua etichetta
 *   (il prototipo lo schiacciava su «Risolto»);
 * - «In attesa associato» e un passo LATERALE: si accende a parte quando il
 *   ticket aspetta l'associato, senza far saltare l'avanzamento;
 * - le etichette sono quelle del codice (`STATUS_LABELS`, rischio R-11).
 */
import React from 'react';
import { STATUS_LABELS } from '../constants';

export const STEPPER_MAIN = ['aperto', 'preso_in_carico', 'in_lavorazione', 'risolto', 'chiuso'] as const;
export const STEPPER_SIDE = 'in_attesa_associato';

export type StepState = 'done' | 'current' | 'todo';

export interface StepperStep {
  status: string;
  label: string;
  state: StepState;
}

export interface StepperModel {
  main: StepperStep[];
  side: StepperStep;
}

/**
 * Modello puro. Con il ticket in attesa il percorso principale resta acceso
 * fino a «In lavorazione» (il lavoro e iniziato ed e sospeso) e il passo
 * laterale e quello corrente. Uno stato sconosciuto non accende nulla.
 */
export const buildStepper = (status: string, closedLabel?: string): StepperModel => {
  const waiting = status === STEPPER_SIDE;
  const idx = waiting
    ? STEPPER_MAIN.indexOf('in_lavorazione')
    : (STEPPER_MAIN as readonly string[]).indexOf(status);
  const main = STEPPER_MAIN.map((s, i): StepperStep => ({
    status: s,
    // «Unito» / «Indesiderato» (displayStatus) al posto di «Chiuso», se il chiamante lo passa.
    label: s === 'chiuso' && closedLabel ? closedLabel : STATUS_LABELS[s] || s,
    state: idx < 0 || i > idx ? 'todo' : i === idx && !waiting ? 'current' : 'done',
  }));
  return {
    main,
    side: { status: STEPPER_SIDE, label: STATUS_LABELS[STEPPER_SIDE], state: waiting ? 'current' : 'todo' },
  };
};

const Step: React.FC<{ step: StepperStep; side?: boolean }> = ({ step, side }) => (
  <li
    className={`s4-step s4-step--${step.state}${side ? ' s4-step--side' : ''}`}
    aria-current={step.state === 'current' ? 'step' : undefined}
  >
    <span className="s4-step__bar" aria-hidden="true" />
    <span className="s4-step__label" title={step.label}>{step.label}</span>
  </li>
);

const StatusStepper: React.FC<{ status: string; closedLabel?: string }> = ({ status, closedLabel }) => {
  const model = buildStepper(status, closedLabel);
  return (
    <div className="s4-stepper">
      <ol className="s4-stepper__main" aria-label="Stato del ticket">
        {model.main.map((s) => (
          <Step key={s.status} step={s} />
        ))}
      </ol>
      <ul className="s4-stepper__side" aria-label="Passo laterale">
        <Step step={model.side} side />
      </ul>
    </div>
  );
};

export default StatusStepper;
